// src/components/PersonalInfoCard.js
import React, { useContext, useState, useCallback } from "react";
import { Grid, Button, Avatar, TextField, useMediaQuery, useTheme, Box, Typography, IconButton, FormControlLabel, Switch } from "@mui/material";
import { FormContext } from "../../../context/FormContext";
import BaseCard from './BaseCard';
import ImageCropper from '../shared/ImageCropper';
import tokens from '../../../theme/tokens';
import components from '../../../theme/components';
import { FormGridContainer } from '../shared/FormFields';
import { useLanguage } from '../../../localization';
import { PhotoCamera, Delete } from '@mui/icons-material';

const PersonalInfoCardComponent = () => {
  const { formData, updateFormData } = useContext(FormContext);
  const { t } = useLanguage();
  // Ensure personalInfo exists with default values
  const personalInfo = formData.personalInfo || {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    photo: '',
    linkedin: '',
    github: '',
    enablePictureSpace: true,
  };
  
  const [expanded, setExpanded] = useState(true);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use useCallback for handleChange
  const handleChange = useCallback((e) => {
    updateFormData("personalInfo", {
      ...personalInfo,
      [e.target.name]: e.target.value,
    });
  }, [updateFormData, personalInfo]);

  // Updated handleUploadPicture to open cropper instead of directly setting image
  const handleUploadPicture = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }
      
      // Check file size (optional, e.g., 5MB limit)
      const maxSizeInMB = 5;
      if (file.size > maxSizeInMB * 1024 * 1024) {
        alert(`Please select an image smaller than ${maxSizeInMB}MB.`);
        return;
      }
      
      setSelectedImageFile(file);
      setCropperOpen(true);
    }
    // Clear the input so the same file can be selected again if needed
    e.target.value = '';
  }, []);

  // Handle cropped image result
  const handleCropComplete = useCallback((croppedImageUrl) => {
    updateFormData("personalInfo", {
      ...personalInfo,
      photo: croppedImageUrl,
    });
    setCropperOpen(false);
    setSelectedImageFile(null);
  }, [updateFormData, personalInfo]);

  // Handle cropper close
  const handleCropperClose = useCallback(() => {
    setCropperOpen(false);
    setSelectedImageFile(null);
  }, []);

  return (
    <>
      <BaseCard 
        title={t('forms.personalInfo.title')} 
        isSelected={true} 
        defaultExpanded={true}
        showRemoveButton={false}
        dragHandle={null}
        expanded={expanded}
        onExpandClick={() => setExpanded(!expanded)}
      >
        <FormGridContainer container spacing={isMobile ? 1 : tokens.spacing.input.gap} isMobile={isMobile}>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar
              src={personalInfo.photo || ''}
              alt={t('forms.personalInfo.profilePicture')}
              sx={{ 
                width: isMobile ? tokens.spacing[10] : tokens.spacing[12],
                height: isMobile ? tokens.spacing[10] : tokens.spacing[12],
                mb: tokens.spacing[2],
                // Ensure the avatar always maintains aspect ratio and fits content properly
                objectFit: 'cover',
              }}
            />
            <Button 
              variant="contained"
              component="label"
              sx={{ mb: tokens.spacing[1] }}
            >
              {t('forms.personalInfo.uploadPicture')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleUploadPicture}
              />
            </Button>
            
            {personalInfo.photo && (
              <Button 
                variant="text"
                size="small"
                onClick={() => {
                  updateFormData("personalInfo", {
                    ...personalInfo,
                    photo: '',
                  });
                }}
                sx={{ 
                  fontSize: tokens.typography.fontSizes.xs,
                  color: tokens.colors.neutral[500],
                  '&:hover': {
                    color: tokens.colors.error,
                  }
                }}
              >
                {t('forms.personalInfo.removePicture')}
              </Button>
            )}
            
            {/* Picture Space Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={personalInfo.enablePictureSpace ?? true}
                  onChange={(e) => {
                    updateFormData("personalInfo", {
                      ...personalInfo,
                      enablePictureSpace: e.target.checked,
                    });
                  }}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: tokens.colors.primary.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: tokens.colors.primary.main,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: tokens.typography.fontSizes.sm }}>
                    {t('forms.personalInfo.enablePictureSpace')}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontSize: tokens.typography.fontSizes.xs, 
                    color: tokens.colors.neutral[600],
                    display: 'block',
                    lineHeight: 1.2,
                    mt: 0.25
                  }}>
                    {t('forms.personalInfo.enablePictureSpaceHelper')}
                  </Typography>
                </Box>
              }
              sx={{ 
                mt: tokens.spacing[2],
                mb: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.firstName')}
              name="firstName"
              value={personalInfo.firstName || ''}
              onChange={handleChange}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.lastName')}
              name="lastName"
              value={personalInfo.lastName || ''}
              onChange={handleChange}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.email')}
              name="email"
              value={personalInfo.email || ''}
              onChange={handleChange}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.phone')}
              name="phone"
              value={personalInfo.phone || ''}
              onChange={handleChange}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.address')}
              name="address"
              value={personalInfo.address || ''}
              onChange={handleChange}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.linkedin')}
              name="linkedin"
              value={personalInfo.linkedin || ''}
              onChange={handleChange}
              placeholder={t('forms.personalInfo.linkedinPlaceholder')}
              sx={components.input.root}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('forms.personalInfo.github')}
              name="github"
              value={personalInfo.github || ''}
              onChange={handleChange}
              placeholder={t('forms.personalInfo.githubPlaceholder')}
              sx={components.input.root}
            />
          </Grid>
        </FormGridContainer>
      </BaseCard>

      {/* Image Cropper Dialog */}
      <ImageCropper
        open={cropperOpen}
        onClose={handleCropperClose}
        imageFile={selectedImageFile}
        onCropComplete={handleCropComplete}
        cropSize={isMobile ? 250 : 300}
      />
    </>
  );
}

const PersonalInfoCard = React.memo(PersonalInfoCardComponent);

export default PersonalInfoCard;
